import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * Landing target for the "Open your building" button in the welcome email.
 * It:
 *  1. Ensures the user is authenticated (otherwise sends them to /login with a redirect).
 *  2. Sets the specified building as the active building on their session.
 *  3. Routes them to the correct dashboard for their role.
 */
export default function WelcomeBuilding() {
  const { hoaId } = useParams();
  const { user, loading, switchBuilding } = useAuth();
  const navigate = useNavigate();
  const [msg, setMsg] = useState('Opening your building…');

  useEffect(() => {
    if (loading) return;
    if (!user) {
      const target = encodeURIComponent(`/welcome-building/${hoaId}`);
      navigate(`/login?next=${target}`, { replace: true });
      return;
    }
    (async () => {
      try {
        setMsg('Switching to your new building…');
        await switchBuilding(hoaId);
      } catch (_) {
        // If the switch fails (e.g., they aren't linked), we still send them to the
        // correct dashboard so they can pick the building manually.
      }
      // Route based on role — trustees/managers → /admin, residents → /dashboard.
      const role = user.role;
      if (['managing_agent', 'agent_admin', 'super_agent', 'general_agent'].includes(role)) {
        navigate('/agent', { replace: true });
      } else if (['property_manager', 'trustee', 'chairman'].includes(role)) {
        navigate('/admin', { replace: true });
      } else if (role === 'platform_owner') {
        // Even Platform Owners land on the building's admin dashboard when arriving
        // via the welcome-email link — the building context is what they came for.
        navigate('/admin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, hoaId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-3" />
        <p className="text-slate-600">{msg}</p>
      </div>
    </div>
  );
}
